import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { STAGE_PREORDER, CAN_PREORDER, PJ_ROLES } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Plus, ClipboardList, LogIn, ArrowRight } from "lucide-react";

export default function PreOrders() {
  const { user } = useAuth();
  const canManage = user && CAN_PREORDER.includes(user.role);
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [draftProjects, setDraftProjects] = useState([]);
  const [umOpen, setUmOpen] = useState(false);
  const [umTarget, setUmTarget] = useState(null);
  const [staff, setStaff] = useState([]);
  const [umAt, setUmAt] = useState("");
  const [umPj, setUmPj] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/preorders", { params: status ? { status } : {} }); setList(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openAdd = async () => {
    const { data } = await api.get("/projects", { params: { stage: "Draft" } });
    setDraftProjects(data); setAddOpen(true);
  };
  const addToPreorder = async (pid) => {
    try { await api.patch(`/projects/${pid}/stage`, { stage: "Menunggu Persetujuan", note: "Diajukan ke Pre Order" }); toast.success("Diajukan ke Pre Order"); setAddOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const advance = async (p) => {
    const idx = STAGE_PREORDER.indexOf(p.stage);
    const next = STAGE_PREORDER[idx + 1];
    if (!next) return;
    try { await api.patch(`/projects/${p.id}/stage`, { stage: next }); toast.success(`Tahap: ${next}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openUnitMasuk = async (p) => {
    setUmTarget(p); setUmAt(""); setUmPj(p.penanggung_jawab_id || "");
    const { data } = await api.get("/users").catch(() => ({ data: [] }));
    setStaff(data.filter((u) => u.active && PJ_ROLES.includes(u.role))); setUmOpen(true);
  };
  const submitUnitMasuk = async () => {
    if (!umAt || !umPj) { toast.error("Tanggal/jam masuk dan penanggung jawab wajib diisi."); return; }
    setBusy(true);
    try {
      if (umPj !== umTarget.penanggung_jawab_id) await api.patch(`/projects/${umTarget.id}/pj`, { penanggung_jawab_id: umPj });
      const { data } = await api.patch(`/projects/${umTarget.id}/stage`, { stage: "On Production", unit_in_at: umAt, note: "Unit masuk" });
      toast.success(`Unit masuk. Repair Order ${data.ro_number} dibuat.`);
      setUmOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const tabs = [["", "Semua"], ...STAGE_PREORDER.map((s) => [s, s])];

  return (
    <div className="space-y-4" data-testid="preorders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Pre Order</h1><p className="text-sm text-muted-foreground">{list.length} proyek</p></div>
        {canManage && <Button onClick={openAdd} data-testid="add-preorder-btn" className="bg-primary hover:bg-red-700"><Plus size={16} className="mr-1.5" /> Ajukan dari Proyek Draft</Button>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {tabs.map(([v, l]) => (
          <button key={v} data-testid={`preorder-tab-${v || "all"}`} onClick={() => { const np = new URLSearchParams(); if (v) np.set("status", v); setParams(np); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${status === v ? "bg-navy text-white border-navy" : "bg-white text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><ClipboardList className="mx-auto mb-2 opacity-40" /> Tidak ada Pre Order.</Card> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((p) => {
              const idx = STAGE_PREORDER.indexOf(p.stage);
              const next = STAGE_PREORDER[idx + 1];
              return (
                <Card key={p.id} data-testid={`preorder-card-${p.project_id}`} className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-k3 text-xs font-semibold text-primary">{p.project_id}</span>
                    <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">{p.stage}</span>
                  </div>
                  <div className="font-semibold">{p.plate} · {p.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{p.brand} {p.model} · {p.work_type}</div>
                  <div className="text-xs">Rencana masuk: {fmtDate(p.plan_in_date)} · Target: {fmtDate(p.target_date)}</div>
                  <div className="text-xs">PJ: {p.penanggung_jawab_name || <span className="text-primary">Belum ada PJ</span>}</div>
                  {canManage && (
                    <div className="flex gap-2 items-center pt-1 flex-wrap">
                      {next && <Button size="sm" className="bg-primary hover:bg-red-700 h-9" data-testid={`preorder-advance-${p.project_id}`} onClick={() => advance(p)}>Lanjut ke {next} <ArrowRight size={13} className="ml-1" /></Button>}
                      {p.stage === "Siap Masuk" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid={`unit-masuk-btn-${p.project_id}`} onClick={() => openUnitMasuk(p)}><LogIn size={14} className="mr-1" /> Unit Masuk</Button>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ajukan ke Pre Order</DialogTitle><DialogDescription>Pilih proyek berstatus Draft.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {draftProjects.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada proyek Draft yang tersedia.</p>}
            {draftProjects.map((p) => (
              <div key={p.id} className="flex items-center justify-between border rounded-md p-2.5">
                <div><div className="font-mono-k3 text-xs text-primary">{p.project_id}</div><div className="text-sm font-medium">{p.plate} · {p.customer_name}</div></div>
                <Button size="sm" onClick={() => addToPreorder(p.id)} data-testid={`add-po-${p.project_id}`} className="bg-primary hover:bg-red-700">Ajukan</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={umOpen} onOpenChange={setUmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Unit Masuk</DialogTitle><DialogDescription>Buat Repair Order (masuk tahap On Production).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Tanggal & Jam Unit Masuk *</Label><Input type="datetime-local" value={umAt} onChange={(e) => setUmAt(e.target.value)} data-testid="unit-masuk-datetime" /></div>
            <div className="space-y-1"><Label>Penanggung Jawab Proyek *</Label>
              <Select value={umPj} onValueChange={setUmPj}>
                <SelectTrigger data-testid="unit-masuk-pj"><SelectValue placeholder="Pilih penanggung jawab" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUmOpen(false)}>Batal</Button>
            <Button onClick={submitUnitMasuk} disabled={busy} data-testid="unit-masuk-submit" className="bg-primary hover:bg-red-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Proses Unit Masuk"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
